import * as PIXI from "pixi.js";
import { Settings, GAME_LAYOUT } from "../../settings";

/**
 * The board as a GRID — where each cell's centre is, for effects that travel
 * from one symbol to another.
 *
 * ── Why this is not "just read the container's position" ──
 *
 * It nearly is, and that is the point: the answer must come from the CELL, not
 * from whatever is currently drawn inside it.
 *
 * The Golden Mystery's first trails measured their endpoints from the cell's
 * `effects` container, which lives inside the mystery frame's
 * `attachment_slot_symbol` — a slot object whose world matrix is written every
 * frame from an ANIMATING skeleton. `bounce` translates `myst_main`, the light
 * chase runs on a loop, and the doors move. So the "centre of the symbol" moved
 * with the animation, and a bolt aimed at it landed wherever that bone happened
 * to be on the frame it was measured.
 *
 * The overlay symbol container is the opposite: React positions it from the
 * layout and nothing animates it. Its origin IS the cell centre, and it stays
 * the cell centre through a resize (see the overlay controllers' resize, which
 * re-lays it out).
 *
 * Same conclusion Pot Collection reached — PotCollectionFeatureExecutor and
 * PotCollectionOrbActivator both resolve a grid point from the overlay
 * container and keep the layout formula only as a fallback.
 *
 * ── Resolved fresh, never cached ──
 *
 * Pot Collection says it out loud: "zeusPos resolved fresh per-trail — not
 * cached here. Orientation/resize changes between trails are reflected
 * immediately." A volley of bolts can outlive a viewport change, so every one
 * asks again.
 */

// Reusable point — one per module, only ever used synchronously.
const _origin = new PIXI.Point(0, 0);

/**
 * A cell's centre, in `layer`'s local space.
 *
 * Goes through global space rather than reading `cellContainer.position`
 * directly, so it is still correct if the layer is ever something other than
 * the cell's own parent. When it IS the parent this reduces to the position.
 *
 * @param   {PIXI.Container} layer         Where the effect is drawn.
 * @param   {PIXI.Container} cellContainer The cell's overlay container.
 * @returns {{x:number,y:number}|null} null when either is gone.
 */
export const cellCenterIn = (layer, cellContainer) => {
  if (!layer || layer.destroyed) return null;
  if (!cellContainer || cellContainer.destroyed) return null;
  try {
    const global = cellContainer.toGlobal(_origin);
    const local = layer.toLocal(global);
    return { x: local.x, y: local.y };
  } catch {
    return null;
  }
};

/**
 * A cell's centre from the LAYOUT alone — the fallback, and the definition.
 *
 * This is the same arithmetic the overlay reel sets use to place their cells, so
 * it agrees with `cellCenterIn` by construction rather than by coincidence:
 *
 *   x = reelSpacing x (reel - (REEL_COUNT - 1) / 2)
 *   y = symbolHeight x row
 *
 * `containerY` — the offset that centres the three visible rows — is deliberately
 * NOT included. It is applied to the flat overlay CONTAINER itself, so a point
 * measured in that container's space must not apply it a second time. Pass
 * `containerOffset: true` for a layer that sits outside the overlay, which is
 * the case Pot Collection's own fallback covers.
 *
 * @param   {number} reelIndex
 * @param   {number} rowIndex
 * @param   {Object} p
 * @param   {number} p.symbolHeight
 * @param   {number} p.reelSpacing
 * @param   {boolean} [p.containerOffset] Include the row-centring offset.
 * @returns {{x:number,y:number}}
 */
export const cellCenterFromLayout = (
  reelIndex,
  rowIndex,
  { symbolHeight, reelSpacing, containerOffset = false } = {},
) => {
  const reelCount = GAME_LAYOUT?.REEL_COUNT ?? Settings.REEL_COUNT;
  const visible = Settings.SYMBOLS_VISIBLE;
  const centerOffset = (reelCount - 1) / 2;
  const x = (reelSpacing || 0) * (reelIndex - centerOffset);
  const base = containerOffset ? -symbolHeight * (0.5 * visible - 0.5) : 0;
  return { x, y: base + symbolHeight * rowIndex };
};

export default cellCenterIn;
